import React from "react";
import styled from "styled-components";
import { Empty } from "antd";

const EmptyState = ({
  icon,
  title,
  subtitle,
  steps = [],
  actions = [],
  ...props
}) => {
  return (
    <EmptyStateContainer {...props}>
      <EmptyStateIcon>{icon}</EmptyStateIcon>
      <EmptyStateTitle>{title}</EmptyStateTitle>
      <EmptyStateSubtitle>{subtitle}</EmptyStateSubtitle>

      {steps.length > 0 && (
        <EmptyStateSteps>
          {steps.map((step, index) => (
            <EmptyStateStep key={index}>
              <div className="step-number">{index + 1}</div>
              <div className="step-text">{step}</div>
            </EmptyStateStep>
          ))}
        </EmptyStateSteps>
      )}

      {actions.length > 0 && <EmptyStateActions>{actions}</EmptyStateActions>}
    </EmptyStateContainer>
  );
};

const EmptyStateContainer = styled.div`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 24px;
  border: 2px solid #e2e8f0;
  margin: 2rem 0;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.08);
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2.5rem 1rem;
    margin: 1rem 0;
    border-radius: 16px;
    width: 100%;
    box-sizing: border-box;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(44, 95, 45, 0.02) 0%,
      transparent 100%
    );
    pointer-events: none;
  }
`;

const EmptyStateIcon = styled.div`
  color: var(--color-primary);
  margin-bottom: 2rem;
  opacity: 0.8;
  position: relative;
  z-index: 1;

  .anticon {
    filter: drop-shadow(0 4px 8px rgba(44, 95, 45, 0.15));
    font-size: 2.98rem !important;
  }

  .anticon svg {
    width: 2.98rem !important;
    height: 2.98rem !important;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;

    .anticon {
      font-size: 2.34rem !important;
    }

    .anticon svg {
      width: 2.34rem !important;
      height: 2.34rem !important;
    }
  }
`;

const EmptyStateTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 0.5rem 0;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const EmptyStateSubtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.1rem;
  color: #475569;
  margin-bottom: 2.5rem;
  text-align: center;
  position: relative;
  z-index: 1;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 1.75rem;
    padding: 0 0.25rem;
    line-height: 1.5;
    max-width: 100%;
  }
`;

const EmptyStateSteps = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 3rem;
  box-shadow: 0 4px 20px rgba(44, 95, 45, 0.08);
  border: 1px solid #e2e8f0;
  position: relative;
  z-index: 1;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  text-align: left;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
    margin-bottom: 1.75rem;
    border-radius: 12px;
    max-width: 100%;
    width: 100%;
  }
`;

const EmptyStateStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    gap: 0.75rem;
    padding: 0.625rem;
    margin-bottom: 0.875rem;
  }

  .step-number {
    background: var(--color-primary);
    color: white !important;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 600;
    flex-shrink: 0;
    margin-top: 2px;

    @media (max-width: 768px) {
      width: 28px;
      height: 28px;
      font-size: 0.9rem;
      margin-top: 0;
    }
  }

  .step-text {
    font-family: var(--font-secondary);
    font-size: 1rem;
    color: #374151;
    line-height: 1.5;
    flex: 1;
    min-width: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;

    @media (max-width: 768px) {
      font-size: 0.9rem;
      line-height: 1.5;
      color: #1e293b;
      font-weight: 500;
    }
  }
`;

const EmptyStateActions = styled.div`
  display: flex !important;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 1000px;
  margin: 2rem auto 0 auto;
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  align-items: center;
  visibility: visible !important;
  opacity: 1 !important;

  .ant-btn {
    height: 44px !important;
    font-size: 1rem !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
    padding: 0 2rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    padding: 0;
    gap: 0.875rem;
    margin-top: 1.5rem;
  }
`;

export default EmptyState;
